/**
 * Result-detail page (route `/results/:resultId`, Req 12.3, 11.3). Loads a single result
 * (`GET /v1/results/{resultId}`) and renders its per-Global_Location timing breakdown, the captured
 * response data (HTTP status / failure reason / failing step / failing assertion, shown only when
 * present), and a reference to the captured screenshot object key.
 *
 * A **trace link is rendered exactly when the result carries a `traceId`** (Req 11.3), pointing at
 * the in-app trace view (`/traces/{traceId}`). Rendering a real signed-URL screenshot image is a
 * documented follow-up (see README); this pass shows the S3 object key as a labelled reference.
 */

import { Link, useParams } from 'react-router-dom';

import { useApiClient } from '../api/use-api-client';
import type { GetResultResponse, ResultDto } from '../api/types';
import { StatusBadge } from '../components/Badges';
import { ErrorState, LoadingState } from '../components/LoadStates';
import { formatMs, formatTimestamp } from '../lib/format';
import { useResource } from '../lib/use-resource';

export function ResultDetailPage(): JSX.Element {
  const client = useApiClient();
  const { resultId } = useParams();
  const id = resultId ?? '';

  const result = useResource<GetResultResponse>(() => client.getResult(id), [client, id]);

  if (result.status === 'loading') {
    return (
      <section>
        <h1>Result detail</h1>
        <LoadingState label="Loading result…" />
      </section>
    );
  }

  if (result.status === 'error') {
    return (
      <section>
        <h1>Result detail</h1>
        <ErrorState summary="Could not load this result." message={result.message} isAuthError={result.isAuthError} />
      </section>
    );
  }

  return <ResultBody result={result.data.result} />;
}

function ResultBody({ result }: { result: ResultDto }): JSX.Element {
  const timingPhases = Object.entries(result.timing);
  const responseFields = responseDataFields(result);

  return (
    <section>
      <p className="muted">
        <Link to={`/checks/${encodeURIComponent(result.checkId)}`}>← Back to check</Link>
      </p>
      <h1>Result detail</h1>

      <div className="card current-state">
        <div className="stat">
          <span className="stat-label">Status</span>
          <span className="stat-value">
            <StatusBadge status={result.status} />
          </span>
        </div>
        <div className="stat">
          <span className="stat-label">Location</span>
          <span className="stat-value">{result.location}</span>
        </div>
        <div className="stat">
          <span className="stat-label">When</span>
          <span className="stat-value">{formatTimestamp(result.timestamp)}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Duration</span>
          <span className="stat-value">{formatMs(result.durationMs)}</span>
        </div>
      </div>

      <h2>Timing breakdown</h2>
      {timingPhases.length > 0 ? (
        <table className="checks-table" aria-label="Timing breakdown">
          <thead>
            <tr>
              <th scope="col">Phase</th>
              <th scope="col">Duration</th>
            </tr>
          </thead>
          <tbody>
            {timingPhases.map(([phase, ms]) => (
              <tr key={phase}>
                <td>{phase}</td>
                <td>{formatMs(ms)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted">No timing breakdown was captured for this result.</p>
      )}

      <h2>Response</h2>
      {responseFields.length > 0 ? (
        <dl className="detail-list">
          {responseFields.map(({ label, value }) => (
            <div key={label} className="detail-row">
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="muted">No additional response data was captured.</p>
      )}

      <h2>Screenshot</h2>
      {result.screenshotS3Key ? (
        <p className="muted">
          Captured screenshot reference: <code>{result.screenshotS3Key}</code>
          <br />
          <span className="muted">Inline preview via a signed URL is a documented follow-up.</span>
        </p>
      ) : (
        <p className="muted">No screenshot was captured for this result.</p>
      )}

      <h2>Trace</h2>
      {result.traceId ? (
        <p>
          <Link to={`/traces/${encodeURIComponent(result.traceId)}`}>View distributed trace</Link>
        </p>
      ) : (
        <p className="muted">No trace was recorded for this result.</p>
      )}
    </section>
  );
}

/** Collect the optional response-data fields that are present, in display order (Req 12.3). */
function responseDataFields(result: ResultDto): { label: string; value: string }[] {
  const fields: { label: string; value: string }[] = [];
  if (result.httpStatus !== undefined) fields.push({ label: 'HTTP status', value: String(result.httpStatus) });
  if (result.failureReason !== undefined) fields.push({ label: 'Failure reason', value: result.failureReason });
  if (result.failingStep !== undefined) fields.push({ label: 'Failing step', value: String(result.failingStep) });
  if (result.failingAssertion !== undefined) {
    fields.push({ label: 'Failing assertion', value: String(result.failingAssertion) });
  }
  return fields;
}
